// ============================================================================
// BESPOKIAN Pharmacist Cockpit - Type Definitions
// INVIOLABLE: All monetary values in integer cents, never floats
// ============================================================================

export type PrescriptionStatus =
  | 'RECEIVED'
  | 'PENDING_VERIFICATION'
  | 'VERIFIED'
  | 'PENDING_DUR'
  | 'DUR_PASSED'
  | 'DUR_OVERRIDE'
  | 'PENDING_FILL'
  | 'FILLING'
  | 'FILLED'
  | 'PENDING_PHARMACIST_CHECK'
  | 'CHECKED'
  | 'READY_FOR_PICKUP'
  | 'PICKED_UP'
  | 'SHIPPED'
  | 'CANCELLED'
  | 'REJECTED';

export type PrescriptionSource = 'ERX' | 'FAX' | 'PHONE' | 'TRANSFER' | 'WRITTEN';

export type DURAlertSeverity = 'LOW' | 'MODERATE' | 'HIGH';

export type DURAlertType =
  | 'DRUG_DRUG_INTERACTION'
  | 'DRUG_ALLERGY'
  | 'DUPLICATE_THERAPY'
  | 'DOSE_TOO_HIGH'
  | 'DOSE_TOO_LOW'
  | 'EARLY_REFILL'
  | 'PREGNANCY_CONTRAINDICATION'
  | 'AGE_CONTRAINDICATION'
  | 'RENAL_DOSE_ADJUSTMENT';

export type PDMPRiskLevel = 'LOW' | 'MODERATE' | 'HIGH' | 'CRITICAL';

export type DEASchedule = 'CII' | 'CIII' | 'CIV' | 'CV' | null;

export interface Patient {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  phone: string;
  email?: string;
  address?: Address;
  allergies: string[];
  insurance?: Insurance;
  createdAt: string;
  updatedAt: string;
}

export interface Address {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface Insurance {
  planName: string;
  memberId: string;
  groupNumber: string;
  bin: string;
  pcn: string;
}

export interface Prescriber {
  id: string;
  firstName: string;
  lastName: string;
  suffix: string;
  npi: string;
  deaNumber?: string;
  stateLicense: string;
  phone: string;
  fax?: string;
  address?: Address;
}

export interface Prescription {
  id: string;
  rxNumber: string;
  patientId: string;
  patientName: string;
  prescriberId: string;
  prescriberName: string;
  drugId: string;
  ndc: string;
  drugName: string;
  strength: string;
  quantity: number;
  quantityUnit: string;
  daysSupply: number;
  refillsAuthorized: number;
  refillsRemaining: number;
  sig: string;
  sigCode: string;
  source: PrescriptionSource;
  status: PrescriptionStatus;
  deaSchedule: DEASchedule;
  isControlled: boolean;
  writtenDate: string;
  expirationDate: string;
  isStatOrder: boolean;
  notes: string;
  patientCopay: number;
  createdAt: string;
  updatedAt: string;
  receivedAt?: string;
  patient?: Patient;
  prescriber?: Prescriber;
  fillData?: FillData;
  hasDurAlerts?: boolean;
  durAlerts?: DURAlert[];
  inventoryOptions?: InventoryLot[];
}

export interface DURAlert {
  id: string;
  prescriptionId: string;
  type: DURAlertType;
  severity: DURAlertSeverity;
  message: string;
  interactingDrug?: string;
  clinicalEffect?: string;
  recommendation?: string;
  createdAt: string;
}

export interface DUROverride {
  alertId: string;
  override: boolean;
  reason: string;
  pharmacistId: string;
  timestamp: string;
}

export interface InventoryLot {
  id: string;
  ndc: string;
  lotNumber: string;
  expirationDate: string;
  quantityAvailable: number;
  unitCostCents: number;
  binLocation: string;
  dscsaVerified: boolean;
  serialNumber?: string;
}

export interface FillData {
  prescriptionId: string;
  lotId: string;
  lotNumber: string;
  expirationDate: string;
  ndc: string;
  ndcScanned: string;
  quantityFilled: number;
  doubleCountVerified?: boolean;
  filledBy: string;
  filledAt: string;
}

export interface PDMPResult {
  prescriptionId: string;
  patientId: string;
  queryTimestamp: string;
  queriedAt: string;
  riskScore: number;
  riskLevel: PDMPRiskLevel;
  requiresReview: boolean;
  totalPrescribers: number;
  totalPharmacies: number;
  totalControlledRx: number;
  daysSinceLastControlled: number | null;
  riskFactors: PDMPRiskFactor[];
  overlappingPrescriptions: OverlappingPrescription[];
  recentHistory: PDMPHistoryEntry[];
  alerts: string[];
}

export interface PDMPRiskFactor {
  type: string;
  description: string;
  severity: 'LOW' | 'MODERATE' | 'HIGH';
  value?: number;
}

export interface PDMPHistoryEntry {
  filledDate: string;
  drugName: string;
  deaSchedule: string;
  quantity: number;
  daysSupply: number;
  prescriberName: string;
  pharmacyName: string;
}

export interface OverlappingPrescription {
  rxNumber: string;
  drugName: string;
  deaSchedule: string;
  quantity: number;
  quantityUnit: string;
  sigCode: string;
  fillDate: string;
  filledDate: string;
  pharmacy: string;
  pharmacyName: string;
  prescriber: string;
  prescriberName: string;
  overlapDays: number;
}

export interface AuditLogEntry {
  id: string;
  prescriptionId: string;
  action: string;
  userId: string;
  userName: string;
  userRole: 'PHARMACIST' | 'TECHNICIAN';
  entityType: string;
  entityId: string;
  details: Record<string, unknown>;
  ipAddress: string;
  timestamp: string;
}

export interface QueueCounts {
  total: number;
  pendingVerification: number;
  pendingDUR: number;
  pendingFill: number;
  pendingCheck: number;
  ready: number;
  urgent: number;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'PHARMACIST' | 'TECHNICIAN';
  licenseNumber?: string;
  npi?: string;
  isActive: boolean;
}

// Utility: Format cents to currency string
export function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

// Utility: Format cents for display without currency symbol
export function formatCents(cents: number): string {
  return (cents / 100).toFixed(2);
}
